import {
  Camera,
  FileText,
  Image as ImageIcon,
  Mic,
  Paperclip,
  Send,
  Square,
  X,
} from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatDuration, useVoiceRecorder } from './use-voice-recorder';

/* ---------------------------------------------------------------------------
   שורת הכתיבה: טקסט, צירוף קבצים (עד 10, עד 10MB), צילום, והקלטה קולית.
   ההקלטה נשלחת מיד כשעוצרים — התמלול חוזר כהודעה של המשתמש.
   --------------------------------------------------------------------------- */

export const MAX_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.docx,.xlsx,.csv,.txt,image/jpeg,image/png,image/webp,application/pdf';

export interface ComposerSend {
  text: string;
  files: File[];
  audio?: Blob;
}

export interface ComposerHandle {
  setText: (text: string) => void;
  focus: () => void;
}

export const Composer = forwardRef<
  ComposerHandle,
  {
    onSend: (input: ComposerSend) => Promise<boolean>;
    disabled?: boolean;
    voice?: boolean;
    busy?: boolean;
  }
>(function Composer({ onSend, disabled = false, voice = true, busy = false }, ref) {
  const { t } = useTranslation('assistant');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    setText: (v) => {
      setText(v);
      textarea.current?.focus();
    },
    focus: () => textarea.current?.focus(),
  }));

  const submit = async (audio?: Blob) => {
    if (disabled || busy) return;
    const trimmed = text.trim();
    if (!trimmed && files.length === 0 && !audio) return;
    setError(null);
    const ok = await onSend({ text: trimmed, files, audio });
    // ההודעה נשמרת בשדה עד שהשרת קיבל אותה — כשל לא מוחק את מה שהוקלד.
    if (ok) {
      setText('');
      setFiles([]);
    }
    textarea.current?.focus();
  };

  const recorder = useVoiceRecorder((audio) => void submit(audio));
  const recording = recorder.state === 'recording';

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);
    const tooBig = incoming.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(t('composer.fileTooLarge', { name: tooBig.name }));
      return;
    }
    const next = [...files, ...incoming];
    if (next.length > MAX_FILES) {
      setError(t('composer.tooManyFiles', { max: MAX_FILES }));
      return;
    }
    setError(null);
    setFiles(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  const voiceError = recorder.state === 'denied' ? t('voice.denied') : null;

  return (
    <div className="border-t border-border bg-surface p-2">
      {files.length ? (
        <ul className="mb-2 flex flex-wrap gap-1.5" aria-label={t('message.attachments')}>
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex max-w-56 items-center gap-1 rounded-full border border-border bg-surface-sunken py-0.5 ps-2 pe-0.5 text-2xs"
            >
              {f.type.startsWith('image/') ? (
                <ImageIcon className="size-3 shrink-0" aria-hidden />
              ) : (
                <FileText className="size-3 shrink-0" aria-hidden />
              )}
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                className="grid size-6 place-items-center rounded-full hover:bg-surface-hover"
                aria-label={t('composer.removeFile', { name: f.name })}
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
              >
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error || voiceError ? (
        <p role="alert" className="mb-1 text-2xs text-danger">
          {error ?? voiceError}
        </p>
      ) : null}

      {recording ? (
        <div className="flex items-center gap-2" role="status" aria-live="polite">
          <span className="size-2.5 animate-pulse rounded-full bg-danger" aria-hidden />
          <span className="flex-1 text-sm tabular-nums">
            {t('voice.recording', { time: formatDuration(recorder.seconds) })}
          </span>
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('voice.cancel')}
            onClick={recorder.cancel}
          >
            <X aria-hidden />
          </Button>
          <Button size="icon" variant="danger" aria-label={t('voice.stop')} onClick={recorder.stop}>
            <Square aria-hidden />
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-1">
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            data-testid="assistant-file-input"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('composer.attach')}
            disabled={disabled}
            onClick={() => fileInput.current?.click()}
          >
            <Paperclip aria-hidden />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="sm:hidden"
            aria-label={t('composer.camera')}
            disabled={disabled}
            onClick={() => cameraInput.current?.click()}
          >
            <Camera aria-hidden />
          </Button>
          <label htmlFor="assistant-input" className="sr-only">
            {t('composer.label')}
          </label>
          <textarea
            id="assistant-input"
            ref={textarea}
            rows={1}
            value={text}
            disabled={disabled}
            placeholder={t('composer.placeholder')}
            aria-describedby="assistant-input-hint"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            className={cn(
              'max-h-40 min-h-11 flex-1 resize-none rounded-(--radius-md) border border-border bg-surface px-3 py-2.5 text-sm text-fg',
              'placeholder:text-fg-subtle field-sizing-content disabled:opacity-60',
            )}
          />
          <span id="assistant-input-hint" className="sr-only">
            {t('composer.hint')}
          </span>
          {voice && recorder.state !== 'unsupported' && !text.trim() && files.length === 0 ? (
            <Button
              size="icon"
              variant="secondary"
              aria-label={t('voice.record')}
              disabled={disabled || busy || recorder.state === 'requesting'}
              onClick={() => void recorder.start()}
            >
              <Mic aria-hidden />
            </Button>
          ) : (
            <Button
              size="icon"
              aria-label={t('composer.send')}
              disabled={disabled || busy || (!text.trim() && files.length === 0)}
              onClick={() => void submit()}
            >
              <Send className="rtl:-scale-x-100" aria-hidden />
            </Button>
          )}
        </div>
      )}
    </div>
  );
});
